import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useAlerts } from "context/AlertContext";
import { IGetNotificationsForUser } from "models/notificationItem.models";
import { IFilterParams } from "models/pagination.models";
import { services } from "services";
import { AlertSeverity } from "utils/constants/alerts.constants";
import {
  QueryAPIKey,
  QueryFetchingStatus,
} from "utils/constants/api.constants";
import handleRetryFailure from "utils/helpers/handleRetryFailure";
import { TErrorData } from "utils/types";

type TUseGetUnreadNotificationCountReturn = {
  status: QueryFetchingStatus;
  error: unknown;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  unreadNotificationCount: number | undefined;
};

export default function useGetUnreadNotificationCount(
  userId: string | undefined,
  params: IFilterParams,
  pollingIntervalMs: number,
): TUseGetUnreadNotificationCountReturn {
  const { t } = useTranslation("common", {
    keyPrefix: "notifications",
  });

  const { showAlert } = useAlerts();

  const { status, error, isLoading, isError, isSuccess, data } = useQuery<
    AxiosResponse<IGetNotificationsForUser>,
    AxiosError<TErrorData>,
    IGetNotificationsForUser
  >({
    queryKey: [QueryAPIKey.GetUnreadNotificationCount, userId],
    queryFn: () =>
      services.notifications.getNotificationsForUser(userId || "", params),
    retry: handleRetryFailure(() =>
      showAlert({
        severity: AlertSeverity.Error,
        message: t("getNotificationsForUserFailure"),
      }),
    ),
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
    refetchInterval: pollingIntervalMs,
    enabled: !!userId,
  });

  const unreadNotificationCount = data?.totalElements;

  return {
    status: status as QueryFetchingStatus,
    error,
    isLoading,
    isError,
    isSuccess,
    unreadNotificationCount,
  };
}
